import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@time-tracker/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. This is not a hypothesis: it was measured
 * in `space-academy-app` (same canon, `activity` table in the local stack, course
 * `06a131d0`) — the course is created, 1.03 s later the unit is created and **in
 * between NO `course/updated` op exists**: the title the author had just written
 * never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the latest checkpoint**. If the checkpoint does not yet contain the
 * newly created row, the row is **ABSENT for that instant** — and an UPDATE against
 * it writes nothing, generates no op and **does not fail**. Here that means a
 * time entry, focus session, or task created just now whose first edit (duration,
 * project, description, timer stop) is lost without a trace: billable data.
 *
 * Why the absence of an error proves absolutely nothing here: the server's
 * write-path deliberately always returns 2xx (a 4xx would block the client's
 * queue forever). That is a DIFFERENT path —there the server rejects and stays
 * silent — but the criterion is the same: a user write disappearing without a trace
 * is the worst failure mode.
 *
 * This module's contract: it writes, it **READS BACK**, it compares. If the value
 * did not land, it retries with backoff; if it still has not landed when the retries
 * are exhausted, it **THROWS** — the caller reports that instead of leaving the user
 * looking at a field (or timer) that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberately) ─────────────────────────
 * Hard DELETEs are not verified: their natural check would be "the row is already
 * gone", and "absent" is precisely the ambiguous state of this window — we cannot
 * distinguish "it was deleted" from "it had not arrived yet". In this repo deletes
 * are soft (`deleted_at`), so they do go through `patchRowVerified` like any other
 * primary-key UPDATE. Bulk updates whose `WHERE` is not by primary key (batch by
 * `project_id`, `category_id`…) are not covered either: "0 rows" can be a legitimate
 * result there.
 *
 * ── `user` is NO LONGER excluded — the previous justification was false ─────
 * This module excluded the `user` row with the argument "the server creates it on
 * registration and it arrives via sync, so it is already in the checkpoint before the
 * first preference". That comment was never verified: it was copied from the canon
 * (`listto-app`) and propagated byte for byte to 10 other repos. Audit
 * 2026-08-13 (`infra/reports/user-exclusion-audit/2026-08-13-summary.md`):
 * all 11 were wrong, including the canon.
 *
 * The root cause of that audit is ALREADY CLOSED (2026-08-13): `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeded the LOCAL row for
 * ANY `uid`, without distinguishing a guest from a real account — today its
 * `createEffect` checks `uid !== VISITOR_ID` and seeds only for the
 * guest (see the guard and its comment there, and the measurement in
 * `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`).
 *
 * That does NOT exclude `user` from this module again. `patchRowVerified` is defense
 * IN DEPTH, not a patch that depended on the seeding bug:
 *   - It covers `updateUserPresenceMutation` (`pulse-timer.tsx` / `idle-guard.tsx`)
 *     and the mutations in `packages/shared/src/users/mutations.ts` (locale,
 *     theme, blur, follow-device-theme, avatar, font) — UPDATE against
 *     a `user` row that may still be absent from the view because of the SAME
 *     checkpoint window, on real accounts just created or after
 *     a guest→real upgrade, with no seeding `createEffect`
 *     involved.
 *   - The guard closes ONE window (the guest's local INSERT competing
 *     with the real row); `patchRowVerified` closes the general window of
 *     "UPDATE against a row the checkpoint has not brought yet",
 *     which exists for `user` just as for any other table in this
 *     module.
 * Do not remove it because "it is no longer needed": the guard and `patchRowVerified`
 * solve different problems and neither replaces the other.
 */

/**
 * Retry backoff. Sums to ≈ 6.05 s: amply covers a checkpoint round trip without
 * leaving a quick interaction —stopping the timer,
 * completing a task— hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

async function sleep(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error from a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when compared against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * Verified `UPDATE … WHERE id = ?`: writes, reads back, compares the
 * patch fields, and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing after the backoff is exhausted.
 *
 * `updated_at` is set by this function on each attempt (and is not compared: it changes
 * on every pass); the `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt += 1) {
    // oxlint-disable-next-line no-await-in-loop -- sequential retry is intentional
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    // oxlint-disable-next-line no-await-in-loop -- sequential retry is intentional
    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    // oxlint-disable-next-line no-await-in-loop -- sequential backoff is intentional
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it.
 *
 * For mutations that are not a simple patch —a transaction that rewrites
 * a project's billing definition from a version of the
 * history— retrying inside the transaction is not valid (it would sleep with
 * the write lock held): wait BEFORE entering, and make a single attempt
 * inside.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt += 1) {
    // oxlint-disable-next-line no-await-in-loop -- sequential retry is intentional
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    // oxlint-disable-next-line no-await-in-loop -- sequential backoff is intentional
    await sleep(wait);
  }
}
